"use client";

/* eslint-disable i18next/no-literal-string */

import { useParams, useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@workspace/ui-web/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@workspace/ui-web/dialog";

import { pathsConfig } from "~/config/paths";

import {
  RESTRICTED_FEATURE_PROMPT_EVENT,
} from "./lib/restricted-feature";

import type { RestrictedFeaturePromptDetail } from "./lib/restricted-feature";

const DEFAULT_TITLE = "Upgrade to unlock this feature";
const DEFAULT_DESCRIPTION =
  "You are currently on the free trial with limited access. This feature requires an active subscription. You can continue exploring the app or go straight to Settings → Billing to upgrade.";

export const RestrictedFeaturePromptProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const router = useRouter();
  const params = useParams();
  const [detail, setDetail] = useState<RestrictedFeaturePromptDetail | null>(
    null,
  );

  useEffect(() => {
    const listener = (event: Event) => {
      const customEvent = event as CustomEvent<RestrictedFeaturePromptDetail>;
      setDetail(customEvent.detail);
    };

    window.addEventListener(RESTRICTED_FEATURE_PROMPT_EVENT, listener);
    return () =>
      window.removeEventListener(RESTRICTED_FEATURE_PROMPT_EVENT, listener);
  }, []);

  const billingPath = useMemo(() => {
    const organization = params.organization?.toString();
    if (organization) {
      return pathsConfig.dashboard.organization(organization).settings.billing;
    }

    return pathsConfig.dashboard.user.settings.billing;
  }, [params.organization]);

  const description = detail?.description
    ? `${detail.description} ${DEFAULT_DESCRIPTION}`
    : detail?.featureName
      ? `${detail.featureName} requires an active subscription. ${DEFAULT_DESCRIPTION}`
      : DEFAULT_DESCRIPTION;

  return (
    <>
      {children}
      <Dialog open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{detail?.title ?? DEFAULT_TITLE}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>
              Continue exploring
            </Button>
            <Button
              onClick={() => {
                setDetail(null);
                router.push(billingPath);
              }}
            >
              Go to Settings → Billing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
